<script setup>
import { onMounted, ref } from 'vue'
import Content from '@/components/Content'
import GarantCard from '@/components/GarantCard'
import Prose from '@/components/Prose'
import GlassCardWrapper from '@/components/GlassCardWrapper'

const props = defineProps(['data', 'meta'])
const prev = props.data.prev.data
const next = props.data.next.data
const program = props.data.program.data

const loaded = ref(false)
onMounted(() => {
  loaded.value = true
})
</script>

<template>
  <Layout :meta="props.meta">
    <div class="relative mx-auto max-w-[1200px]">
      <InertiaLink
        href="/program"
        class="absolute top-0 left-32 z-20 hidden h-11 w-11 items-center justify-center rounded-full border border-transparent bg-white/10 text-white duration-200 hover:border-yellow hover:text-yellow lg:flex"
      >
        <SvgArrowRight class="w-[15px] rotate-180 group-hover:-translate-x-2" />
      </InertiaLink>
      <div
        class="relative z-10 -mb-10 mt-32 flex skew-y-[-6deg] flex-col items-center justify-center gap-2 lg:mt-64"
      >
        <div
          class="delay-250 bg-white px-8 py-1 text-center text-[24px] uppercase text-dark duration-500 lg:text-[32px]"
          :class="{
            '-translate-x-32 opacity-0': !loaded,
          }"
        >
          SPOLU pro
        </div>
        <h1
          class="flex items-center justify-center bg-yellow py-2 px-8 text-center text-[28px] font-extrabold uppercase text-dark duration-500 md:text-[36px] lg:text-[48px]"
          v-html="program.headline"
          :class="{
            'translate-x-32 opacity-0': !loaded,
          }"
        ></h1>
      </div>
    </div>

    <Content
      class="delay-500 duration-500"
      :class="{
        'opacity-0': !loaded,
      }"
    >
      <h2
        v-if="program.subheadline"
        class="mb-[40px] max-w-2xl border-b border-dark/10 pb-[40px] text-center text-[24px] font-semibold uppercase lg:mb-[60px] lg:pb-[60px] lg:text-[28px]"
        v-html="program.subheadline"
      ></h2>

      <div class="mb-8 text-center text-lg font-bold uppercase text-dark/60">
        Garantují
      </div>
      <div
        class="mb-[60px] flex flex-wrap justify-center gap-12 lg:-mx-16 lg:gap-16 lg:px-4"
        v-if="program.garants.length"
      >
        <GarantCard
          v-for="garant in program.garants"
          :key="garant.slug"
          :data="garant"
          :small="program.garants.length > 3"
        />
      </div>

      <p v-html="program.perex" class="mt-4 mb-8 text-lg"></p>

      <div
        v-if="program.content.replace(/(<\/?[^>]+(>|$)|&nbsp;|\s)/g, '')"
        class="w-full border-t border-dark/10 pt-8 pb-20"
      >
        <Prose v-html="program.content" />
      </div>

      <div
        class="mt-[20px] flex flex-col items-center justify-center gap-8 gap-y-8 pb-20"
      >
        <div
          v-for="section in program.sections"
          :key="`section-${section.id}`"
          class="flex flex-col items-center justify-center"
        >
          <!-- <h2
            v-if="section.headline"
            class="mb-11 bg-white py-0.5 px-4 text-left text-[26px] font-bold uppercase"
            v-html="section.headline"
          ></h2> -->
          <div class="flex flex-col gap-8">
            <div
              v-for="(point, p) in section.points"
              :key="`point-${point.id}`"
              class="items-left flex flex-col justify-center border-l-2 border-dark/10 pl-4 text-left"
            >
              <!-- <div
                v-if="p !== 0"
                class="mb-6 w-32 border-b border-dark/10"
              ></div> -->
              <p
                class="mb-1 font-semibold"
                v-if="point.headline"
                v-html="point.headline"
              ></p>
              <p v-if="point.content" class="mt-4" v-html="point.content"></p>
            </div>
          </div>
        </div>
      </div>
    </Content>

    <div class="pt-12 lg:pt-32">
      <div class="cards mx-auto grid max-w-7xl grid-cols-2 gap-y-12 py-16">
        <GlassCardWrapper height-class="h-[90px] lg:h-[160px]">
          <InertiaLink
            class="group absolute inset-0 flex items-center justify-center px-10 pt-2 shadow-xl lg:gap-10 lg:pt-0 xl:px-16"
            :href="`/program/${prev.slug}`"
          >
            <span
              class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-transparent bg-white/10 duration-200 group-hover:border-[#FCDA2A]"
            >
              <SvgArrowRight class="rotate-180 text-yellow" />
            </span>
            <div
              class="flex h-full w-0 flex-col items-start justify-center text-[20px] uppercase leading-tight text-white md:text-[24px] lg:relative lg:w-[calc(100%-80px)] xl:text-[28px]"
            >
              <div
                class="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 skew-y-[-6deg] whitespace-nowrap bg-yellow px-4 py-0.5 text-sm font-black uppercase text-dark lg:left-0 lg:translate-x-0 lg:py-1 lg:text-[20px]"
              >
                {{ prev.priority }}
              </div>
              <div class="hidden lg:block">
                <div>SPOLU PRO</div>
                <div
                  class="w-full grow-0 overflow-hidden font-extrabold line-clamp-2"
                  v-html="prev.headline"
                ></div>
              </div>
            </div>
          </InertiaLink>
        </GlassCardWrapper>
        <GlassCardWrapper height-class="h-[90px] lg:h-[160px]">
          <InertiaLink
            class="group absolute inset-0 flex items-center justify-center px-10 pt-2 shadow-xl lg:gap-10 lg:pt-0 xl:px-16"
            :href="`/program/${next.slug}`"
          >
            <div
              class="flex h-full w-0 flex-col items-start justify-center text-[20px] uppercase leading-tight text-white md:text-[24px] lg:relative lg:w-[calc(100%-80px)] xl:text-[28px]"
            >
              <div
                class="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 skew-y-[-6deg] whitespace-nowrap bg-yellow px-4 py-0.5 text-sm font-black uppercase text-dark lg:left-0 lg:translate-x-0 lg:py-1 lg:text-[20px]"
              >
                {{ next.priority }}
              </div>
              <div class="hidden lg:block">
                <div>SPOLU PRO</div>
                <div
                  class="w-full grow-0 overflow-hidden font-extrabold line-clamp-2"
                  v-html="next.headline"
                ></div>
              </div>
            </div>
            <span
              class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-transparent bg-white/10 duration-200 group-hover:border-[#FCDA2A]"
            >
              <SvgArrowRight class="text-yellow" />
            </span>
          </InertiaLink>
        </GlassCardWrapper>
      </div>
      <div class="mt-4 flex justify-center">
        <InertiaLink
          href="/program"
          class="h-15 group flex items-center gap-4 border-2 border-[#FCDA2A] px-8 py-3 font-bold uppercase text-[#FCDA2A]"
        >
          <SvgArrowRight
            class="rotate-180 transition-all duration-200 group-hover:-translate-x-2"
          />
          <span>Zpět na celý program</span>
        </InertiaLink>
      </div>
    </div>
  </Layout>
</template>
